/**
 * WhatsApp drafts and "marked as sent", plus the step-8 privacy settings — ADR-001 §10.6 ("no WhatsApp API in
 * version 1": a wa.me link with the text; we record that the person composed it and MARKED it sent, never that it
 * was delivered), §7.4 (`engagement_log.via = 'whatsapp_link'`, `nudges`), §11.10; guide §8 ("A pop-up builds the
 * message from ticked points; buttons Copy message and Open in WhatsApp"); the prototype's WA / waItems / waBuild
 * / waModal / waSend (docs/prototype.html L3195–3222, L3932–3940); Settings → Recordings and privacy (L4436–4438);
 * operator decision 15 + ADR-002 S7 (audio retention is a setting, default forever, and the consent text is
 * generated from it); ADR-004 §7 (retention is a system setting — `settings.system`); scope decision 13 (the tone
 * reason is for managers only).
 *
 * Two kinds of message, one composer — the server writes the text so the wording is the same everywhere:
 *  - to a CLIENT (`client_id`, optionally for an engagement activity such as the WhatsApp group): a manager or the
 *    client's SPOC; the phone is the client's primary contact's;
 *  - to a TEAM MEMBER (`to_user_id`): a manager nudging an Ops person about their open items (the prototype's
 *    "Message {first} on WhatsApp", grouped points: timesheet, overdue, escalations…).
 */
import { z } from 'zod';
import { Timestamp, Uuid } from './common.js';
import { ClientRef } from './clients.js';
import { ActivityKey, EngagementLog } from './engagement.js';
import { UserRef } from './users.js';
/**
 * GET /whatsapp/draft and POST /whatsapp/sent with a team member as the target (`user_id`) need this capability —
 * the founder and the associate director (managers; 403 for anyone else). A client target follows the client's scope.
 * The routes admit every client worker (`anyOf`), so this rule lives here, read by the handler and the contract test.
 */
export const WHATSAPP_TEAM_TARGET_CAPABILITY = 'team.read';
// ============================================================================ GET /whatsapp/draft
/** Prototype tones: "Friendly" · "Firm" · "Thank and nudge". */
export const WhatsAppTone = z.enum(['friendly', 'firm', 'thanks']).meta({ id: 'WhatsAppTone' });
export const WHATSAPP_TONE_LABEL = { friendly: 'Friendly', firm: 'Firm', thanks: 'Thank and nudge' };
/** The prototype's groups, in its order (a person) — a client message has one group, `points`. */
export const WhatsAppPointGroup = z
    .enum(['timesheet', 'overdue', 'do_first', 'escalations', 'requests_late', 'proof_to_fix', 'due_today', 'blocked', 'done_well', 'points'])
    .meta({ id: 'WhatsAppPointGroup' });
export const WHATSAPP_GROUP_LABEL = {
    timesheet: 'Timesheet',
    overdue: 'Overdue',
    do_first: 'Do first today',
    escalations: 'Escalations',
    requests_late: 'Client requests past their time',
    proof_to_fix: 'Proof to fix',
    due_today: 'Due today',
    blocked: 'Blocked',
    done_well: 'Done well',
    points: 'Points',
};
const PointId = z.string().regex(/^[a-z_]+:[A-Za-z0-9-]{1,64}$/, 'A point id from the draft, e.g. overdue:<task id>');
export const WhatsAppDraftQuery = z
    .object({
    client_id: Uuid.optional(),
    /** With `client_id`: the engagement activity the message is for (e.g. `wa`, the WhatsApp group). */
    activity_key: ActivityKey.optional(),
    to_user_id: Uuid.optional(),
    /** Comma-separated point ids to tick; omitted = the server's default ticks (`on`). */
    points: z
        .string()
        .max(4000)
        .transform((s, ctx) => {
        const parts = s.split(',').map((p) => p.trim()).filter(Boolean);
        const bad = parts.filter((p) => !PointId.safeParse(p).success);
        if (bad.length) {
            ctx.addIssue({ code: 'custom', message: `Unknown point id(s): ${bad.slice(0, 3).join(', ')}` });
            return z.NEVER;
        }
        return parts;
    })
        .optional(),
    tone: WhatsAppTone.default('friendly'),
})
    .strict()
    .refine((q) => !!q.client_id !== !!q.to_user_id, { path: ['client_id'], message: 'Message a client or a team member (one of them)' })
    .refine((q) => !q.activity_key || !!q.client_id, { path: ['activity_key'], message: 'An activity needs the client' });
export const WhatsAppPoint = z
    .object({ group: WhatsAppPointGroup, id: z.string(), line: z.string(), on: z.boolean() })
    .meta({ id: 'WhatsAppPoint' });
export const WhatsAppDraft = z
    .object({
    to: z.object({
        kind: z.enum(['client', 'person']),
        client: ClientRef.nullable(),
        /** The team member (person), or `null`. */
        user: UserRef.nullable(),
        /** Who the message is addressed to: the person's first name, or the client's primary contact. */
        name: z.string(),
    }),
    tone: WhatsAppTone,
    /** The composed message ("*Group*" headings and "• line" items, the tone's opening and closing). */
    text: z.string(),
    /**
     * `https://wa.me/<digits>?text=<text>`, or `null` when there is no phone number (the button is disabled with
     * an explanation — ADR §10.6). The person may edit the text; the SPA then rebuilds `text=` on `wa_base`.
     */
    wa_url: z.string().nullable(),
    wa_base: z.string().nullable(),
    phone_present: z.boolean(),
    /** Plain-English reason when `phone_present` is false, e.g. "Add Farah's phone number on the client page first". */
    no_phone_reason: z.string().nullable(),
    points: z.array(WhatsAppPoint),
    /** The prototype's "Last nudged {at}" (person) / the cell's last WhatsApp log (client activity). */
    last_marked_at: Timestamp.nullable(),
})
    .meta({ id: 'WhatsAppDraft' });
// ============================================================================ POST /whatsapp/sent
/**
 * "Copy message" / "Open in WhatsApp" record that the caller composed it and marked it sent. Only a hash of the
 * text is kept (never the message). With `client_id` + `activity_key`, it logs the engagement activity done today
 * (`via: 'whatsapp_link'`); with `to_user_id`, it records a nudge ("Nudged {at}").
 */
export const MarkWhatsAppSentBody = z
    .object({
    client_id: Uuid.optional(),
    activity_key: ActivityKey.optional(),
    to_user_id: Uuid.optional(),
    /** SHA-256 of the text as sent, lowercase hex. */
    text_hash: z.string().regex(/^[0-9a-f]{64}$/, 'Send the SHA-256 of the message, as 64 hex characters'),
    /** How many points were ticked (the nudge's count). */
    points_count: z.number().int().nonnegative().max(200).optional(),
    /** How it left: the copy button or the wa.me link. */
    via: z.enum(['copy', 'wa_link']),
})
    .strict()
    .refine((b) => !!b.client_id !== !!b.to_user_id, { path: ['client_id'], message: 'Say who it went to: a client or a team member' })
    .refine((b) => !b.activity_key || !!b.client_id, { path: ['activity_key'], message: 'An activity needs the client' });
/** A WhatsApp nudge to a team member; a repeat the same day increments `count`. */
export const WhatsAppNudge = z
    .object({
    id: Uuid,
    to: UserRef,
    by: UserRef,
    points_count: z.number().int().nonnegative(),
    count: z.number().int().positive(),
    first_at: Timestamp,
    last_at: Timestamp,
})
    .meta({ id: 'WhatsAppNudge' });
export const MarkWhatsAppSentResponse = z
    .object({
    marked_at: Timestamp,
    /** Always "Marked as sent by you" — never "Sent" or "Delivered". */
    label: z.literal('Marked as sent by you'),
    engagement_log: EngagementLog.nullable(),
    nudge: WhatsAppNudge.nullable(),
})
    .meta({ id: 'MarkWhatsAppSentResponse' });
// ============================================================================ privacy settings (step 8)
/** Retention choices for stored meeting audio; `null` = forever (the default). */
export const AudioRetentionDays = z.union([z.literal(30), z.literal(90), z.literal(180), z.null()]).meta({ id: 'AudioRetentionDays' });
export const PrivacySettings = z
    .object({
    recordings: z.object({
        /** Ask "participants informed" before an audio upload (prototype CFG.requireConsent, default on). */
        require_consent: z.boolean(),
        retention_days: AudioRetentionDays,
        /** The meeting consent wording (ADR-002 §29.3): says the audio goes to the browser's speech service and only the submitted text is saved. Fixed; it does not follow retention_days. */
        consent_text: z.string(),
    }),
    tone: z.object({
        /** Members see the tone LABEL (never the reason) on their clients' emails. Default off. */
        show_label_to_members: z.boolean(),
    }),
    /** Bumped on every Settings change; send it back as If-Match. Shared with the other Settings groups. */
    config_version: z.number().int().positive(),
    updated_at: Timestamp.nullable(),
    updated_by: UserRef.nullable(),
})
    .meta({ id: 'PrivacySettings' });
/**
 * PATCH /settings/privacy. `recordings.retention_days` needs `settings.system` (ADR-004 §7; the founder) — `403`
 * otherwise. Shortening retention deletes older audio at the next nightly run, and from then re-transcribing those
 * meetings is no longer possible; the meetings themselves stay.
 */
export const PatchPrivacySettingsBody = z
    .object({
    recordings: z
        .object({ require_consent: z.boolean().optional(), retention_days: AudioRetentionDays.optional() })
        .strict()
        .optional(),
    tone: z.object({ show_label_to_members: z.boolean().optional() }).strict().optional(),
})
    .strict()
    .refine((b) => Object.values(b).some((g) => g && Object.values(g).some((v) => v !== undefined)), { message: 'Change at least one setting' });
//# sourceMappingURL=whatsapp.js.map